import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Loader2, AlertCircle, RefreshCw, CopyCheck } from "lucide-react";
import { Button } from "@/shared/components/form/Button";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { canSeePublicAccountsTracking } from "../utils/publicAccountsPermissions";
import { AccountDetailModal } from "../components/AccountDetailModal";
import { AccountsWalletsTrackingTable } from "../components/AccountsWalletsTrackingTable";
import { AuditoriaPagination } from "@/modules/dashboard/auditoria/components/AuditoriaPagination";
import { useAccountsWalletsTrackingPage } from "../hooks/useAccountsWalletsTrackingPage";
import { SEARCH_DEBOUNCE_MS } from "../constants/public-accounts.constants";
import useTranslations from "@/shared/lang/useTranslations";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";

export const AccountsWalletsTrackingPage = () => {
  const { t } = useTranslations("accountsWallets");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_analitica") },
      { label: tGlobal("sidebar_menu_accounts_tracking"), to: "/app/accounts-wallets-tracking" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const { user } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    rows,
    totalItems,
    page,
    pageSize,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    hasNoAccess,
    paginationOptions,
    pageNumbers,
    handlePageChange,
    handleItemsPerPageChange,
    refetch,
  } = useAccountsWalletsTrackingPage(searchParam);

  const [detailAccountId, setDetailAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchParam(searchValue);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchValue]);

  const handleViewDetail = useCallback((id: string) => {
    setDetailAccountId(id);
  }, []);

  if (!canSeePublicAccountsTracking(user)) {
    return <NoAccessView />;
  }

  if (hasNoAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-6 p-2">
      <div className="flex items-center justify-between">
        <header>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            <CopyCheck className="h-8 w-8 text-green-500 dark:text-green-400" />
            {t("tracking_title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("tracking_subtitle")}
          </p>
        </header>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("tracking_search_placeholder")}
            className="pl-3 pr-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            aria-label={t("tracking_search_aria")}
          />
          <Button
            variant="outline"
            onClick={refetch}
            disabled={isLoading}
            type="button"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
            />
            {t("tracking_button_refresh")}
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-12 flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("tracking_loading")}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden">
          <AccountsWalletsTrackingTable
            rows={rows}
            totalItems={totalItems}
            itemsPerPage={pageSize}
            paginationOptions={paginationOptions}
            onItemsPerPageChange={handleItemsPerPageChange}
            onViewDetail={handleViewDetail}
          />
          <AuditoriaPagination
            currentPage={page}
            lastPage={lastPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            pageNumbers={pageNumbers}
            from={from}
            to={to}
            total={totalItems}
            onPageChange={handlePageChange}
            recordsText={t("tracking_pagination_records", { from, to, total: totalItems })}
            totalPagesText={lastPage != null ? t("tracking_pagination_total_pages", { lastPage }) : undefined}
            firstPageTitle={t("tracking_pagination_first_page")}
            prevPageTitle={t("tracking_pagination_prev_page")}
            nextPageTitle={t("tracking_pagination_next_page")}
            lastPageTitle={t("tracking_pagination_last_page")}
          />
        </div>
      )}

      <AccountDetailModal
        isOpen={detailAccountId !== null}
        onClose={() => setDetailAccountId(null)}
        accountId={detailAccountId}
        showCopyButton={false}
      />
    </div>
  );
};
